import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import HeadModal from "../components/HeadModal";
import headService from "../services/HeadService";
import { showSuccessToast, showErrorToast } from "../utils/AlertUtils";
import { useLoading } from "../context/LoadingContext";
import HeadListing from '../components/HeadsListing';
import ConfirmationModal from '../components/ConfirmationModal';
import guLocale from "../locales/gu";

const Head = () => {
  const locale = guLocale.confirmationModal;
  const headListingLocale = guLocale.headListing;
  const { setIsLoading } = useLoading();
  const [filterText, setFilterText] = useState(""); // State for filter text
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [currentHead, setCurrentHead] = useState(null); // State for storing data for editing
  const [data, setData] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [headIdToDelete, setHeadIdToDelete] = useState(null);
  const [totalCashBalance, setTotalCashBalance] = useState(0);
  const [totalBankBalance, setTotalBankBalance] = useState(0);

  const fetchListingData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await headService.getHeads();
      let cashTotal = 0;
      let bankTotal = 0;
      if(response && response.statusCode === 200 && response.data) {
        setData(response.data);
        response.data.forEach(item => {
          cashTotal += parseFloat(item.opening_balance_cash) || 0;
          bankTotal += parseFloat(item.opening_balance_bank) || 0;
        });
        setTotalCashBalance(cashTotal);
        setTotalBankBalance(bankTotal);
      }
    } catch (error) {
      console.error("Error fetching listing data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    fetchListingData();
  }, [fetchListingData]);

  const columns = [
    { name: headListingLocale.headName, selector: (row) => row.head_name, sortable: true },
    { name: headListingLocale.cashBalance, selector: (row) => row.opening_balance_cash },
    { name: headListingLocale.bankBalance, selector: (row) => row.opening_balance_bank },
    {
      name: headListingLocale.action,
      cell: (row) => (
        <>
          <button className="btn btn-primary btn-sm mr-2" onClick={() => handleEdit(row)}>
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteClick(row._id)}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      Object.values(item).some((value) =>
        value.toString().toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [data, filterText]);

  const handleFilterChange = useCallback((e) => {
    setFilterText(e.target.value); // Only update filterText state
  }, []);

  const handleEdit = (head) => {
    setCurrentHead(head); // Set data for editing
    setShowModal(true); // Show modal
  };

  const handleDeleteClick = (id) => {
    setHeadIdToDelete(id);
    setIsConfirmModalOpen(true);
  };
  
  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      const response = await headService.deleteHead(headIdToDelete);
      if (response && response.statusCode === 200) {
        showSuccessToast(response.message);
        fetchListingData();
      }
    } catch (error) {
      console.error("Error deleting head", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
      setIsConfirmModalOpen(false);
      setHeadIdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmModalOpen(false);
    setHeadIdToDelete(null);
  };

  const handleModalSubmit = async (formData) => {
    try {
      setIsLoading(true);
      let response;
      if (currentHead) {
        // Edit existing head
        const data = {
          head_name: formData[0].head_name,
          opening_balance_bank: formData[0].opening_balance_bank,
          opening_balance_cash: formData[0].opening_balance_cash,
        };
        response = await headService.updateHead(currentHead._id, data);
      } else {
        // Add new head
        response = await headService.createHead(formData);
      }
      if (response && (response.statusCode === 201 || response.statusCode === 200)) {
        showSuccessToast(response.message);
        fetchListingData();
      }
      setShowModal(false); // Close modal after submit
    } catch (error) {
      console.error("Error submitting form data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setCurrentHead(null);
  };
  return (
    <div>
      <HeadListing
        columns={columns}
        data={filteredData}
        filterText={filterText}
        handleFilterChange={handleFilterChange}
        handleShow={handleShow}
        totalCashBalance={totalCashBalance}
        totalBankBalance={totalBankBalance}
      />
      <HeadModal
        showModal={showModal}
        handleClose={handleClose}
        handleSubmit={handleModalSubmit}
        initialData={currentHead}
      />
      <ConfirmationModal
        show={isConfirmModalOpen}
        title= 'હેડ કાઢી નાખો'
        message={locale.deleteHeadConfirmationMessage}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default Head;